<template>
  <perfect-scrollbar class="perfect-scrollbar" :class="themeClass">
    <div class="menu-wrap" :class="{ '!overflow-y-visible': activeWizardKey }">
      <ul class="menu">
        <template v-for="(item, index) in items">
          <li
            v-if="item && !item.isHidden && checkPermissions(item)"
            :id="item.key"
            :key="index"
            :data-cy="`menu-link-${item.page}`"
            class="menu-item"
            :class="{ ...getMenuItemClasses(item), 'active-wizard': activeWizardKey === item?.key }"
          >
            <div v-if="!item.page && !item.link && item.isDivider" class="menu-item-link-line" />

            <div
              v-else-if="!item.page && !item.link"
              :key="`key-${index}`"
              class="menu-item-section"
            >
              <h5 class="menu-item-section-text">{{ $t(item.translate) }}</h5>
            </div>

            <div
              v-else-if="item.page === 'Products' && getPermission['catalog.categories']"
              class="menu-item-link-block cursor-pointer items-center"
              @click="clearCategory"
            >
              <div class="flex items-center">
                <h5 class="menu-item-link-text">{{ $t(item.translate) }}</h5>
              </div>
              <div class="category-arrow cursor-pointer items-center">
                <OurSvgIcon :src="icons.AngleRight" :class="{ 'rotate-90': showCategories }" />
              </div>
            </div>

            <OurLink
              v-else
              class="menu-item-link"
              :url="getUrl(item)"
              :target-blank="getTarget(item)"
              :route="null"
              no-focus-ring
              @click="onClickItem(item)"
            >
              <div class="menu-item-link-block items-center">
                <h5 class="menu-item-link-text" v-html="$t(item.translate)" />
              </div>
            </OurLink>
            <div v-if="categoriesLink" class="menu-item">
              <div v-if="categories.length && showCategories && index === 1" class="category-list">
                <CategoriesList
                  :key="catalogCategoriesListKey"
                  :items="categories"
                  @clickCategory="onClickCategory"
                />
              </div>
            </div>
          </li>
        </template>
        <li
          v-if="isCatalogVersionToggleVisible"
          class="menu-item"
          @click="onChangeCatalogUiVersion"
        >
          <div class="cursor-pointer px-4 py-2.5 text-xs text-blue-400">
            {{ $t(`page.catalog.version.${catalogVersionToggleHintKey}`) }}
          </div>
        </li>
      </ul>
    </div>
  </perfect-scrollbar>
</template>

<script>
import { mapGetters, mapMutations, mapState, mapActions } from "vuex";
import { getThemeClass, getBasePath } from "@/services/_bit/UiService";
import "vue2-perfect-scrollbar/dist/vue2-perfect-scrollbar.css";
import layoutMixin from "./mixins";
import permissionsMixin from "@/mixins/permissions.mixin";
import { PerfectScrollbar } from "vue2-perfect-scrollbar";
import OurLink from "@/components/_bit/ButtonLink";
import OurSvgIcon from "@/components/_bit/IconSvg";
import CategoriesList from "@/views/dashboard/_components/CategoriesList.vue";
import { dashboardCategoriesQuery } from "@/views/dashboard/gql/queries";
import apolloMixin from "@/mixins/apollo.mixin";
import { nextTick } from "vue";
import { setCatalogUiVersionMutation } from "@/views/products/index/gql/mutations";

export default {
  name: "MainSubMenu",

  components: {
    CategoriesList,
    PerfectScrollbar,
    OurLink,
    OurSvgIcon,
  },

  mixins: [layoutMixin, apolloMixin, permissionsMixin],

  props: {
    items: {
      type: Array,
      required: true,
    },

    showIcon: {
      type: Boolean,
      default: false,
    },

    iconColor: {
      type: String,
      default: "gray",
    },

    activeItem: {
      type: Boolean,
      default: false,
    },
    categoriesLink: {
      type: Boolean,
      default: true,
    },

    showCatalogVersionToggle: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      legacyCategories: [],
      showCategories: false,
      perPage: 3,
      page: 1,
    };
  },

  computed: {
    ...mapState("layout", ["isAnimation"]),

    ...mapGetters("breakpoint", ["isTabletDevice", "isMobileDevice"]),
    ...mapGetters("user", ["getPermission", "activeWizardKey", "getCatalogUiVersion"]),
    ...mapGetters("catalogCategories", ["getNavigationCategories"]),

    isLegacyCatalogUi() {
      return String(this.getCatalogUiVersion ?? "").toUpperCase() !== "NEW";
    },

    catalogCategoriesListKey() {
      return `${String(this.getCatalogUiVersion ?? "unknown")}-${this.categories.length}`;
    },

    catalogVersionToggleHintKey() {
      return String(this.getCatalogUiVersion ?? "").toLowerCase() === "new" ? "new" : "old";
    },

    categories() {
      return this.isLegacyCatalogUi ? this.legacyCategories : this.getNavigationCategories;
    },

    isCatalogVersionToggleVisible() {
      return (
        this.showCatalogVersionToggle &&
        this.$route.name === "Products" &&
        this.getPermission["catalog.categories"]
      );
    },

    themeClass() {
      return getThemeClass();
    },

    icons: () => ({
      AngleRight: require("@/components/_bit/IconSvg/icons/Angle-right.svg"),
    }),
  },

  watch: {
    $route: {
      handler(to, from) {
        if (from?.name) {
          nextTick(() => this.CLOSE_MOBILE_SUB_BLOCK());
        }

        this.syncShowCategoriesState(to);
      },
      immediate: true,
    },
  },

  mounted() {
    if (this.categoriesLink || this.showCatalogVersionToggle) {
      this.getData();
    }
  },

  methods: {
    ...mapActions("catalogCategories", [
      "fetchNewCatalogCategories",
      "invalidateNewCatalogCategories",
    ]),
    ...mapMutations("layout", [
      "ENABLE_ANIMATION",
      "CLOSE_ASIDE_SUB_BLOCK",
      "CLOSE_MOBILE_SUB_BLOCK",
    ]),
    ...mapMutations("product", ["SET_CATEGORY"]),
    ...mapMutations("user", ["SET_CATALOG_UI_VERSION"]),

    syncShowCategoriesState(to = this.$route) {
      if (this.isMobileDevice) {
        this.showCategories = false;

        return;
      }

      this.showCategories = to.name === "Products";
    },

    getMenuItemClasses({ page }) {
      const parentRoute = page ? this.$router.resolve({ name: page }).href : page;
      const routePath = this.$route.path;
      const path = `${getBasePath()}${routePath}`;

      return {
        "menu-item-active":
          (this.checkCurrentPage(page) || path.includes(parentRoute)) && this.activeItem,
      };
    },

    getUrl({ link }) {
      if (link) return link.url;
    },

    getTarget({ link }) {
      if (link) return link.targetBlank;
    },

    onClickItem(item) {
      if (this.isTabletDevice) {
        this.CLOSE_ASIDE_SUB_BLOCK();
      }

      if (item?.link?.url) {
        if (item.link.targetBlank) {
          window.open(item.link.url, "_blank");
        } else {
          window.location.assign(item.link.url);
        }

        return;
      }

      if (!item.page || this.checkCurrentPage(item.page)) {
        if (this.isMobileDevice) this.CLOSE_MOBILE_SUB_BLOCK();

        return;
      }

      this.$router.push({ name: item.page }).catch(() => {});

      if (this.isMobileDevice) this.CLOSE_MOBILE_SUB_BLOCK();
    },

    async getData() {
      if (this.isLegacyCatalogUi) {
        const categoriesResponse = await this.$get(
          dashboardCategoriesQuery,
          {},
          { withLoader: true }
        );

        if (!categoriesResponse) {
          return;
        }

        this.legacyCategories = categoriesResponse.productCategories || [];

        if (this.legacyCategories?.[0]?.id !== "promotional-offers") {
          this.legacyCategories.unshift({
            id: "promotional-offers",
            description: "Акційні пропозиції",
            parent: null,
            children: [],
          });
        }

        if (this.legacyCategories?.[1]?.id !== "wholesale") {
          this.legacyCategories.unshift({
            id: "wholesale",
            description: "Опт",
            parent: null,
            children: [],
          });
        }

        return;
      }

      await this.fetchNewCatalogCategories({
        withLoader: true,
      });
    },

    async stripProductsCatalogCategoryQuery() {
      if (this.$route.name !== "Products" || !this.$route.query.category) return;

      const nextQuery = Object.fromEntries(
        Object.entries(this.$route.query).filter(([key]) => key !== "category" && key !== "page")
      );

      await this.$router.replace({ name: "Products", query: nextQuery }).catch(() => {});
    },

    clearCategory() {
      // if (this.$route.query.category) {
      //   // this.$router.push({ name: "Products", query: {} });
      //   this.SET_CATEGORY({});
      // }

      if (this.$route.name !== "Products") {
        this.$router.push({ name: "Products" });
      }

      this.showCategories = !this.showCategories;
    },

    onClickCategory(category) {
      if (+category.id === 643) {
        if (this.$router.currentRoute.name === "FleaMarket") return;

        this.$router.push({ name: "FleaMarket" });
      } else {
        this.SET_CATEGORY(category);

        if (this.$router.currentRoute.name !== "Products") {
          this.$router.push({ name: "Products" });
        }
      }

      // this.CLOSE_MOBILE_SUB_BLOCK();
    },

    onChangeCatalogUiVersion() {
      this.setCatalogUiVersion(this.isLegacyCatalogUi ? "NEW" : "OLD");
    },

    async setCatalogUiVersion(version) {
      const { setCatalogUiVersion } = await this.$post(
        setCatalogUiVersionMutation,
        { version: version },
        { withLoader: false, withNotify: false }
      );

      if (setCatalogUiVersion) {
        this.SET_CATALOG_UI_VERSION(version);
        this.SET_CATEGORY({});
        await this.stripProductsCatalogCategoryQuery();
        await this.invalidateNewCatalogCategories();
        await this.getData();
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.category-arrow {
  @apply flex;
  margin-left: auto !important;
}
.category-list:deep {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 80vh;
  background-color: #f3f4f6;

  .categories-list .group:hover {
    background-color: transparent;
  }

  .categories-list .group .category {
    padding: 10px 5px;
  }
}
.perfect-scrollbar {
  @apply h-full;

  .menu-wrap {
    @apply overflow-y-auto;

    .menu {
      &-item {
        &-section {
          @apply px-6 py-2 md:px-4 md:pt-0;
          @apply cursor-auto;

          &-text {
            @apply text-sm font-normal text-gray-400;
          }
        }

        &-link {
          @apply flex w-full cursor-pointer;

          &:deep(a) {
            @apply w-full;
          }

          &-block {
            @apply w-full;
            @apply flex items-center space-x-3;
            @apply px-6 md:px-4;

            &:hover {
              @apply bg-gray-200/50 md:rounded-lg;
              @apply text-gray-800;
            }
          }

          &-text {
            @apply flex items-center;
            @apply py-2.5;
            @apply text-base font-normal text-gray-600;
            @apply overflow-hidden text-ellipsis whitespace-nowrap;
          }

          &-line {
            @apply h-px w-auto;
            @apply mx-3 my-2;
            @apply bg-gray-700;
          }
        }
      }

      &-item-active {
        @apply bg-gray-200 md:rounded-lg;

        .menu-item-link-text {
          @apply text-gray-800;
        }
      }

      .menu-item + .menu-item {
        @apply mt-px;
      }
    }
  }
}
</style>
